/** baby: Craft Constellation Workbench: dive + connector chain (Architecture B). */

const ASSET = '/ninxl/craft/constellation'
const still = (id: string) => `${ASSET}/still/${id}.png`
const clip = (id: string) => `${ASSET}/vid/${id}.mp4`
const conn = (n: number) => `${ASSET}/vid/conn${n}.mp4`

export interface CraftConstellationSection {
  id: string
  label: string
  still: string
  clip: string
  scroll: number
  linger: number
  accent: string
  eyebrow: string
  title: string
  body: string
  tags: string[]
  /** baby: SVG node position in a 1000×600 viewBox */
  node: { x: number; y: number }
}

export interface CraftConstellationConfig {
  diveScroll: number
  connScroll: number
  crossfade: number
  /**
   * baby: Sensitivity knob for wheel scrub (mapped to ~px of delta).
   * Not a document scroll spacer: track height stays one mat tall.
   */
  flightBudgetVh: number
  hint: string
  sections: CraftConstellationSection[]
  connectors: string[]
}

export const CRAFT_CONSTELLATION_CONFIG: CraftConstellationConfig = {
  // baby: Relative pacing on the 0-1 progress timeline.
  diveScroll: 0.62,
  connScroll: 0.32,
  crossfade: 0.08,
  // baby: Higher = more wheel travel to finish flight (still no blank page gap).
  flightBudgetVh: 1.8,
  hint: 'scroll the workbench',
  sections: [
    {
      id: 'desk',
      label: 'Desk',
      still: still('desk'),
      clip: clip('desk'),
      scroll: 0.72,
      linger: 0.32,
      accent: '#21728d',
      eyebrow: 'Private desktop',
      title: 'Your machine. Your agent.',
      body: 'Fold into a local NINX workstation: chat and tools that stay on your Mac or PC by default.',
      tags: ['Mac + Windows', 'Local', '~20 MB'],
      node: { x: 120, y: 140 },
    },
    {
      id: 'models',
      label: 'Models',
      still: still('models'),
      clip: clip('models'),
      scroll: 0.65,
      linger: 0.3,
      accent: '#1a5f75',
      eyebrow: 'On-device models',
      title: 'Warm models on disk.',
      body: 'Paper-stack weights and whisper reels stay on your hardware: inference without a silent cloud hop.',
      tags: ['Whisper', 'Local LLM', 'No upload'],
      node: { x: 320, y: 220 },
    },
    {
      id: 'inkflow',
      label: 'InkFlow',
      still: still('inkflow'),
      clip: clip('inkflow'),
      scroll: 0.65,
      linger: 0.3,
      accent: '#3d8fa8',
      eyebrow: 'Voice writing',
      title: 'Speak. It writes.',
      body: 'Dictate once. InkFlow pastes at the cursor in Slack, Docs, Mail, or your IDE.',
      tags: ['InkFlow', 'Tray', 'Cursor paste'],
      node: { x: 520, y: 120 },
    },
    {
      id: 'tools',
      label: 'Tools',
      still: still('tools'),
      clip: clip('tools'),
      scroll: 0.62,
      linger: 0.28,
      accent: '#4aa3bc',
      eyebrow: 'Approved connectors',
      title: 'Shell, browser, MCP.',
      body: 'Twine-linked tools for Gmail, brokers, and internal MCP: every run gated by approval.',
      tags: ['Shell', 'MCP', 'Plugins'],
      node: { x: 700, y: 240 },
    },
    {
      id: 'vault',
      label: 'Vault',
      still: still('vault'),
      clip: clip('vault'),
      scroll: 0.65,
      linger: 0.3,
      accent: '#186678',
      eyebrow: 'Private by default',
      title: 'Zero data leaves.',
      body: 'A sealed kraft vault: history, files, and keys stay on-device unless you opt in.',
      tags: ['Locked', 'Opt-in cloud', 'Your keys'],
      node: { x: 860, y: 160 },
    },
    {
      id: 'download',
      label: 'Download',
      still: still('download'),
      clip: clip('download'),
      scroll: 0.7,
      linger: 0.32,
      accent: '#21728d',
      eyebrow: 'Get NINX',
      title: 'Put it on your desk.',
      body: 'Download for Mac or Windows and start private in minutes.',
      tags: ['Free to try', 'Apple Silicon', 'Windows'],
      node: { x: 920, y: 380 },
    },
  ],
  connectors: [conn(1), conn(2), conn(3), conn(4), conn(5)],
}
